import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { AGENTS } from "@/lib/agents";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Bot, Sparkles, CheckCircle2, Loader2, Mail, Lock, User } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Algo deu errado. Tente novamente.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e?.msg ? e.msg : JSON.stringify(e))).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default function Login() {
  const { user, loading, setUser } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [li, setLi] = useState({ email: "", password: "" });
  const [rg, setRg] = useState({ nome: "", email: "", password: "" });

  useEffect(() => {
    if (!loading && user) navigate("/dashboard");
  }, [user, loading, navigate]);

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const doLogin = async () => {
    setBusy(true);
    try {
      const res = await api.post("/auth/login", li);
      setUser(res.data);
      toast.success("Bem-vindo de volta!");
      navigate("/dashboard");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const doRegister = async () => {
    setBusy(true);
    try {
      const res = await api.post("/auth/register", rg);
      setUser(res.data);
      toast.success("Conta criada! Vamos começar.");
      navigate("/dashboard");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const perks = [
    "Fluxo completo: Briefing → Estratégia → Ideias → Copy → Publicação",
    "Pacote pronto adaptado para Instagram, TikTok e Facebook",
    "Calendário editorial e diagnóstico do Analista de IA",
  ];

  return (
    <div className="agency-bg min-h-screen text-slate-100 flex flex-col">
      <header className="w-full max-w-7xl mx-auto px-6 py-6 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center">
            <Bot className="w-5 h-5 text-emerald-400" />
          </div>
          <span className="font-display font-extrabold text-lg tracking-tight">AGÊNCIA<span className="text-emerald-400">.AI</span></span>
        </div>
      </header>

      <main className="flex-1 max-w-7xl mx-auto px-6 w-full grid lg:grid-cols-2 gap-12 items-center py-8">
        <div>
          <div className="inline-flex items-center gap-2 overline text-emerald-400 border border-emerald-500/30 rounded-full px-3 py-1 mb-6">
            <Sparkles className="w-3.5 h-3.5" /> Agência de IA autônoma
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-none">
            Uma equipe de <span className="text-emerald-400">7 agentes de IA</span> cuidando das suas redes
          </h1>
          <p className="mt-6 text-base text-slate-400 max-w-xl leading-relaxed">
            Diga <span className="text-slate-200 font-medium">"Criar conteúdo para minha marca"</span> e o Orquestrador delega cada etapa ao especialista certo — para Instagram, TikTok e Facebook.
          </p>
          <ul className="mt-8 space-y-3">
            {perks.map((p) => (
              <li key={p} className="flex items-start gap-3 text-sm text-slate-300">
                <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" /> {p}
              </li>
            ))}
          </ul>
          <div className="mt-8 grid grid-cols-4 gap-2 max-w-sm">
            {AGENTS.map((a) => {
              const Icon = a.icon;
              return (
                <div key={a.id} className="aspect-square rounded-xl flex items-center justify-center glass"
                  title={a.nome} style={{ borderColor: a.color + "44" }}>
                  <Icon className="w-5 h-5" style={{ color: a.color }} />
                </div>
              );
            })}
          </div>
        </div>

        <div className="glass rounded-3xl p-6 sm:p-8 w-full max-w-md mx-auto">
          <h2 className="font-display text-2xl font-bold tracking-tight mb-1">Acesse a agência</h2>
          <p className="text-sm text-slate-400 mb-6">Entre com Google ou email para começar.</p>

          <Button data-testid="login-btn-google" onClick={handleGoogle} variant="outline"
            className="w-full border-slate-700 bg-slate-900/60 hover:border-emerald-500/50 hover:brightness-110 transition-all mb-5">
            <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
            Continuar com Google
          </Button>

          <div className="flex items-center gap-3 mb-5">
            <div className="h-px flex-1 bg-slate-800" />
            <span className="text-xs text-slate-500">ou</span>
            <div className="h-px flex-1 bg-slate-800" />
          </div>

          <Tabs defaultValue="login">
            <TabsList className="grid grid-cols-2 w-full bg-slate-900/60 border border-slate-800">
              <TabsTrigger data-testid="tab-login" value="login" className="data-[state=active]:bg-emerald-500 data-[state=active]:text-slate-950">Entrar</TabsTrigger>
              <TabsTrigger data-testid="tab-register" value="register" className="data-[state=active]:bg-emerald-500 data-[state=active]:text-slate-950">Criar conta</TabsTrigger>
            </TabsList>

            <TabsContent value="login" className="space-y-3 mt-4">
              <Field icon={Mail} testid="login-email" type="email" placeholder="seu@email.com"
                value={li.email} onChange={(v) => setLi({ ...li, email: v })} />
              <Field icon={Lock} testid="login-password" type="password" placeholder="Senha"
                value={li.password} onChange={(v) => setLi({ ...li, password: v })} />
              <Button data-testid="btn-login-submit" onClick={doLogin} disabled={busy}
                className="w-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold hover:brightness-110 transition-all">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Entrar"}
              </Button>
            </TabsContent>

            <TabsContent value="register" className="space-y-3 mt-4">
              <Field icon={User} testid="register-nome" placeholder="Seu nome"
                value={rg.nome} onChange={(v) => setRg({ ...rg, nome: v })} />
              <Field icon={Mail} testid="register-email" type="email" placeholder="seu@email.com"
                value={rg.email} onChange={(v) => setRg({ ...rg, email: v })} />
              <Field icon={Lock} testid="register-password" type="password" placeholder="Senha (mín. 6 caracteres)"
                value={rg.password} onChange={(v) => setRg({ ...rg, password: v })} />
              <Button data-testid="btn-register-submit" onClick={doRegister} disabled={busy}
                className="w-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold hover:brightness-110 transition-all">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Criar conta"}
              </Button>
            </TabsContent>
          </Tabs>
        </div>
      </main>
    </div>
  );
}

function Field({ icon: Icon, testid, type = "text", placeholder, value, onChange }) {
  return (
    <div className="relative">
      <Icon className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
      <Input data-testid={`input-${testid}`} type={type} placeholder={placeholder} value={value}
        onChange={(e) => onChange(e.target.value)}
        className="pl-9 bg-slate-900/60 border-slate-700 focus-visible:ring-emerald-500/40" />
    </div>
  );
}
